import { useState, useEffect, useCallback } from "react";
import { json2csv } from "json-2-csv";

import {
  Grid,
  TableCell,
  TableRow,
  IconButton,
  Collapse,
  Box,
  Typography,
  useTheme,
  Tooltip,
  Chip,
  Button,
  Avatar,
  TableContainer,
  TableHead,
  TableBody,
  Table,
  TablePagination,
  Card as MuiCard,
  CardHeader,
  CardContent,
  CardActions
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { faXmark, faPencil } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import { sourceDescriptions } from "@aclymatepackages/audit-logs"; // kept for backward compat with pre-migration logs
import {
  ToggleButtons,
  LoadingButton,
  TextField
} from "@aclymatepackages/atoms";
import { editRowData } from "@aclymatepackages/array-immutability-helpers";
import { formatDate } from "@aclymatepackages/formatters";
import { nameToFirstName } from "@aclymatepackages/converters";

import CircularProgressWithLabel from "../../atoms/loading/CircularProgressWithLabel";
import FeatureErrorBoundary from "../../atoms/FeatureErrorBoundary";
import Card from "../../atoms/mui/Card";
import CsvEmailDownloadButton from "../../atoms/buttons/CsvEmailDownloadButton";
import IdDisplayChip from "../../atoms/IdDisplayChip";

import SlideLayout from "../../layouts/SlideLayout";

import SortableTable, {
  makeColumnObj
} from "../../modules/tables/SortableTable";

import WarningPopup from "../../atoms/notifications/WarningPopup";

import SmartUploadReviewDialog from "../../modules/smart-upload/SmartUploadReviewDialog";
import { REVIEWABLE_TASK_STATUSES } from "../../modules/smart-upload/reviewLineGates";
import {
  buildCsvRetryEdits,
  retryErroredCsvRows
} from "../../../helpers/smartUpload";
import { isReviewDrivenCsvFamily } from "../../../helpers/smartUploadLineRequirements";

import { useCompanyAuditLogs } from "../../../helpers/hooks/companyData";
import {
  useCachedDisplayData,
  useCollectionDataListener,
  getTaskFileDownloadUrl,
  useCachedFirebaseCrud,
  useAuth,
  getFileMetadata
} from "../../../helpers/firebase";
import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";
import { getAccountId } from "../../../helpers/accountHelpers";
import {
  getFieldOptionsByTaskType,
  getExpectedTypeForField,
  isFieldRequired,
  isValueValidForType,
  getTypeErrorMessage,
  getTaskTypesDetails,
  getTaskStatusDetails,
  formatTimestamp,
  getFileNameFromPath,
  transactionsFieldOptions,
  shippingFieldOptions,
  waterCsvFieldOptions,
  steamCsvFieldOptions,
  chillWaterCsvFieldOptions,
  refrigerantsCsvFieldOptions,
  SMART_UPLOAD_FIELD_OPTIONS_BY_FAMILY
} from "../../../helpers/components/tasks";
import { getUrlSearchParams } from "../../../helpers/otherHelpers";

const useHandleDownload = () => {
  const { activateSnackbar } = usePlatformLayoutContext();

  return async ({ filePath, fileBucket }) => {
    const fullFilePath = `${filePath.replace("processed-data", "raw-data")}`;

    try {
      const url = await getTaskFileDownloadUrl(fileBucket, fullFilePath);
      return window.open(url, "_blank");
    } catch (err) {
      activateSnackbar({
        message: "Failed to get download URL.",
        alert: "error"
      });
    }
  };
};

const JsonDisplay = ({ data }) => {
  const { palette } = useTheme();

  return (
    <Box
      sx={{
        backgroundColor: palette.backgroundGray.main,
        p: 2,
        borderRadius: 1,
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        fontSize: "0.875rem",
        color: palette.text.primary,
        wordBreak: "break-all",
        overflowWrap: "anywhere"
      }}
    >
      {JSON.stringify(data, null, 2)}
    </Box>
  );
};

const AuditLogRow = ({ users, ...log }) => {
  const { timestamp, userId, source, label } = log;
  const { dataBefore, dataAfter, ...generalInfo } = log;
  const { palette } = useTheme();

  const [open, setOpen] = useState(false);

  const getUserDisplay = () => {
    if (!userId || !Array.isArray(users)) {
      return "Unknown";
    }

    const userObj = users.find((u) => u.uid === userId);
    const { name } = userObj || {};

    return name || userId;
  };

  const cellsArray = [
    formatTimestamp(timestamp),
    getUserDisplay(),
    label || sourceDescriptions[source] || "Unknown"
  ];

  return (
    <>
      <TableRow hover>
        {cellsArray.map((value, idx) => (
          <TableCell key={`audit-log-cell-${idx}`}>
            <Typography variant="body2" color={palette.text.primary}>
              {value}
            </Typography>
          </TableCell>
        ))}
        <TableCell align="right">
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            aria-label="expand row"
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <Typography
                    variant="h6"
                    color={palette.text.primary}
                    gutterBottom
                  >
                    General Information
                  </Typography>
                  <JsonDisplay data={generalInfo} />
                </Grid>
                <Grid item xs={4}>
                  <Typography
                    variant="h6"
                    color={palette.text.primary}
                    gutterBottom
                  >
                    Data Before
                  </Typography>
                  <JsonDisplay data={dataBefore} />
                </Grid>
                <Grid item xs={4}>
                  <Typography
                    variant="h6"
                    color={palette.text.primary}
                    gutterBottom
                  >
                    Data After
                  </Typography>
                  <JsonDisplay data={dataAfter} />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const AuditLogTable = ({ auditLogs, error }) => {
  const [employees = []] = useCollectionDataListener("employees");
  const users = employees.map(({ authUid, name, email }) => ({
    uid: authUid,
    name,
    email
  }));

  const rows = Array.isArray(auditLogs)
    ? auditLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    : [];

  const tableColumns = [
    makeColumnObj("TIMESTAMP", "timestamp", true),
    makeColumnObj("USER", "user", true),
    makeColumnObj("SOURCE", "source", true),
    makeColumnObj("", "", false)
  ];

  return error ? (
    <Box display="flex" justifyContent="center" alignItems="center" p={2}>
      <Typography color="error">Failed to load audit logs.</Typography>
    </Box>
  ) : rows.length === 0 ? (
    <Box display="flex" justifyContent="center" alignItems="center" p={2}>
      <Typography variant="body1">No audit logs found.</Typography>
    </Box>
  ) : (
    <SortableTable
      rows={rows}
      rowComponent={(log) => (
        <AuditLogRow key={`audit-log-${log.id}`} {...log} users={users} />
      )}
      columns={tableColumns}
    />
  );
};

const TaskDocRow = ({ taskDoc, onViewSummary, onReview }) => {
  const {
    id: taskDocId,
    startTime,
    type,
    filePath,
    fileBucket,
    status,
    currentChunk,
    totalChunks,
    totalTasks,
    totalTasksProcessed,
    document_family: documentFamily
  } = taskDoc;

  const [user] = useAuth();
  const { displayName, email } = user || {};
  const { palette } = useTheme();
  const [erroredTaskDataRows] = useCachedDisplayData(
    `tasks/${taskDocId}/taskData`,
    [["error", "==", true]]
  );
  // Smart Upload rows that extracted no usable lines land here, never in
  // erroredTaskDataRows above -- a distinct, non-error terminal state (ticket
  // #376 spec) that still needs its own visibility in this existing tracking
  // UI rather than showing the same "no errors" checkmark a clean batch gets.
  const [needsAttentionTaskDataRows] = useCachedDisplayData(
    `tasks/${taskDocId}/taskData`,
    [["status", "==", "extracted"]]
  );

  const numErroredTaskDataRows = erroredTaskDataRows.filter(
    ({ ignoreInReprocess }) => !ignoreInReprocess
  ).length;
  const numNeedsAttentionRows = needsAttentionTaskDataRows.length;
  const isFinishedWithErrors =
    status === "finished" && !!numErroredTaskDataRows;

  const { icon, color, summary } = getTaskStatusDetails(
    taskDoc,
    numErroredTaskDataRows,
    palette,
    numNeedsAttentionRows
  );
  const {
    Icon,
    color: typeColor,
    name: taskName
  } = getTaskTypesDetails({
    taskType: type,
    documentFamily,
    palette,
    iconColor: "white"
  });
  const fileName = getFileNameFromPath(filePath, type, documentFamily);

  const onPlaidCsvDownloadBtnClick = async () =>
    await fetchOurApi({
      path: "/transactions/send-plaid-transactions-csv-email",
      method: "POST",
      data: {
        taskDocId,
        email,
        firstName: nameToFirstName(displayName),
        taskDate: formatDate(startTime)
      },
      callback: (res) => res
    });

  const officeCsvTypes = [
    "purchased-water-csv",
    "steam-csv",
    "chill-water-csv",
    "refrigerants-csv"
  ];
  const isOfficeCsvType = officeCsvTypes.includes(type);

  const isReviewableSmartUpload =
    type === "smart-upload" && REVIEWABLE_TASK_STATUSES.includes(status);
  // A finished batch is still reachable (#42) -- Smart Upload fallback entry
  // points (a no-activity row, a failed file) stay open even after every
  // other row in the batch is done, so "Open" re-enters the same review
  // dialog, which renders read-only except for those CTAs in finished mode.
  const isFinishedSmartUpload =
    type === "smart-upload" && status === "finished" && !isReviewDrivenCsvFamily(documentFamily);

  const showRowActionButton = () => {
    if (isReviewableSmartUpload) {
      return (
        <Button
          variant="contained"
          size="small"
          color="primary"
          sx={{ color: palette.common.white }}
          onClick={onReview}
        >
          Review
        </Button>
      );
    }

    if (isFinishedSmartUpload) {
      return (
        <Button variant="outlined" size="small" onClick={onReview}>
          Open
        </Button>
      );
    }

    if (
      status === "finished" &&
      ((fileBucket && filePath) || isOfficeCsvType)
    ) {
      return (
        <Button
          variant="contained"
          size="small"
          color={isFinishedWithErrors ? "error" : "primary"}
          onClick={onViewSummary}
        >
          View Summary
        </Button>
      );
    }

    if (type === "plaid-transactions") {
      return (
        <CsvEmailDownloadButton
          tooltip="Download bank transactions CSV"
          onDownloadBtnClick={onPlaidCsvDownloadBtnClick}
        />
      );
    }

    return <></>;
  };

  const isActiveTask = status === "uploading" || status === "running";
  const progressPercent =
    isActiveTask && totalChunks
      ? Math.round((currentChunk / totalChunks) * 100)
      : 0;

  return (
    <TableRow>
      <TableCell>
        {isActiveTask ? (
          <Tooltip
            title={
              totalTasks > 0
                ? `${totalTasksProcessed || 0}/${totalTasks} rows`
                : summary
            }
          >
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgressWithLabel value={progressPercent} size={32} />
              {totalTasks > 0 && (
                <Typography variant="caption" color="textSecondary">
                  {totalTasksProcessed || 0}/{totalTasks} rows
                </Typography>
              )}
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title={summary}>
            <FontAwesomeIcon
              icon={icon}
              color={color}
              style={{ width: "20px", height: "20px" }}
            />
          </Tooltip>
        )}
      </TableCell>
      <TableCell>{formatTimestamp(startTime)}</TableCell>
      <TableCell>
        <Chip
          style={{
            backgroundColor: typeColor,
            color: "white"
          }}
          label={taskName}
          icon={Icon}
          size="small"
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2">{fileName}</Typography>
      </TableCell>
      <TableCell>
        <IdDisplayChip id={taskDocId} type="task" />
      </TableCell>
      <TableCell>{showRowActionButton()}</TableCell>
    </TableRow>
  );
};

export const ErroredTaskDataRow = ({
  row,
  tableHeaders,
  isEditing,
  handleCellEdit,
  handleIgnoreRow,
  setDisplayedRows
}) => {
  const { palette } = useTheme();
  const { ignoreInReprocess, isEdited, displayedRowIndex } = row;
  const editRowDataFunc = editRowData(displayedRowIndex, setDisplayedRows);

  const { taskType, document_family: documentFamily } = row;
  const fieldOptions = getFieldOptionsByTaskType(taskType, documentFamily);

  const renderStatusCell = (colIndex) => (
    <TableCell
      key={`task-data-cell-${displayedRowIndex}-${colIndex}`}
      align="center"
    >
      {ignoreInReprocess ? (
        <FontAwesomeIcon
          icon={faXmark}
          color={palette.error.main}
          style={{ marginLeft: 8 }}
        />
      ) : isEdited ? (
        <FontAwesomeIcon
          icon={faPencil}
          color={palette.primary.main}
          style={{ marginLeft: 8 }}
        />
      ) : null}
    </TableCell>
  );

  const renderActionsCell = (colIndex) => (
    <TableCell
      key={`task-data-cell-${displayedRowIndex}-${colIndex}`}
      align="right"
    >
      <Tooltip
        title={
          ignoreInReprocess
            ? "Include in reprocessing"
            : "Ignore during reprocessing"
        }
      >
        <IconButton
          onClick={() => handleIgnoreRow(editRowDataFunc, ignoreInReprocess)}
          size="small"
        >
          <DeleteForeverIcon color={ignoreInReprocess ? "error" : "action"} />
        </IconButton>
      </Tooltip>
    </TableCell>
  );

  const getValidationProps = (field, storageKey) => {
    if (!isEditing) {
      return { error: false, helperText: undefined };
    }

    const expectedType = getExpectedTypeForField(fieldOptions, field);
    if (!expectedType) {
      return { error: false, helperText: undefined };
    }

    const required = isFieldRequired(fieldOptions, field);
    const isValid = isValueValidForType(row[storageKey], expectedType, required);
    if (isValid) {
      return { error: false, helperText: undefined };
    }

    return {
      error: true,
      helperText: getTypeErrorMessage(expectedType)
    };
  };

  const renderDataCell = (label, field, storageKey, align, colIndex) => {
    const { error, helperText } = getValidationProps(field, storageKey);

    const formatCellValue = (value) => {
      if (value instanceof Date) {
        return formatDate(value);
      }
      if (value === null || value === undefined) {
        return "";
      }
      return String(value);
    };

    return (
      <TableCell
        key={`task-data-cell-${displayedRowIndex}-${colIndex}`}
        align={align}
      >
        {isEditing ? (
          <TextField
            value={row[storageKey] ?? ""}
            setValue={(value) =>
              handleCellEdit(editRowDataFunc, storageKey, value)
            }
            variant="standard"
            fullWidth
            disabled={ignoreInReprocess}
            id={`task-data-cell-text-field-${displayedRowIndex}-${colIndex}-${label}`}
            error={error}
            helperText={helperText}
          />
        ) : (
          formatCellValue(row[storageKey])
        )}
      </TableCell>
    );
  };

  const renderTableRowCell = (label, field, storageKey, align, colIndex) => {
    if (field === "status") {
      return renderStatusCell(colIndex);
    }

    if (field === "actions") {
      return renderActionsCell(colIndex);
    }

    return renderDataCell(label, field, storageKey, align, colIndex);
  };

  return (
    <TableRow key={`task-data-row-${displayedRowIndex}`}>
      {tableHeaders.map(({ label, field, storageKey = field, align }, colIndex) =>
        renderTableRowCell(label, field, storageKey, align, colIndex)
      )}
    </TableRow>
  );
};

export const TaskDetailSlide = ({ task, onClose }) => {
  const {
    type,
    filePath,
    startTime,
    endTime,
    totalTasks,
    totalTasksProcessed,
    id: taskDocId,
    taskData,
    fileBucket,
    document_family: documentFamily
  } = task;
  const { taskDataColumnNames } = taskData || {};
  // A general Smart Upload batch is reviewed in SmartUploadReviewDialog, and
  // its task/row docs are server-write-only (firestore.rules), so the
  // edit-and-reprocess flow the older importers use doesn't apply to it.
  const isGeneralSmartUpload =
    type === "smart-upload" &&
    (!documentFamily || documentFamily === "unclassified");
  // Transactions and shipping CSVs are reviewed line by line (ticket #393):
  // their task and row docs are server-write-only too, so a reprocess goes
  // through csv-retry-errored-rows instead of client writes.
  const isReviewDrivenCsv =
    type === "smart-upload" && isReviewDrivenCsvFamily(documentFamily);

  const handleDownload = useHandleDownload();
  const { palette } = useTheme();
  const { activateSnackbar } = usePlatformLayoutContext();
  const companyId = getAccountId();
  const [erroredTaskDataRows, isErroredTaskDataLoading] = useCachedDisplayData(
    `tasks/${taskDocId}/taskData`,
    [["error", "==", true]]
  );
  // Self-review finding (ticket #376): without this, a Smart Upload batch
  // made up only of extracted/no-result rows showed "without any errors"
  // here even though the row-list-level indicator (above) correctly flagged
  // it -- the two summaries disagreed on the same batch.
  const [needsAttentionTaskDataRows] = useCachedDisplayData(
    `tasks/${taskDocId}/taskData`,
    [["status", "==", "extracted"]]
  );
  const { updateCollectionDoc } = useCachedFirebaseCrud();

  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isEditing, setIsEditing] = useState(false);
  const [displayedRows, setDisplayedRows] = useState([]);
  const [fileMetadata, setFileMetadata] = useState(null);

  const { Icon, color } = getTaskTypesDetails({
    taskType: type,
    documentFamily,
    palette,
    fontSize: "medium"
  });
  const fileName = getFileNameFromPath(filePath, type, documentFamily);
  const date = formatTimestamp(startTime);
  // Phase 3 PR review finding (ticket #376): this must filter by
  // !ignoreInReprocess the same way the row-list view's numErroredTaskDataRows
  // does above -- otherwise a batch whose only errors are all
  // ignoreInReprocess shows "has errors" here while the list view (correctly)
  // shows it as clean, and the two views contradict each other on one batch.
  const hasErrors =
    !isErroredTaskDataLoading &&
    erroredTaskDataRows.filter(({ ignoreInReprocess }) => !ignoreInReprocess)
      .length > 0;
  const { summary } = getTaskStatusDetails(
    task,
    displayedRows.length,
    palette,
    needsAttentionTaskDataRows.length
  );

  const hasModifiedRows = displayedRows.some(
    ({ isEdited, ignoreInReprocess }) => isEdited || ignoreInReprocess
  );

  const statusHeaderObj =
    hasModifiedRows || isEditing
      ? [{ label: "", field: "status", align: "center" }]
      : [];

  const buildTasksDataColumnHeaderObjs = () => {
    const { customMetadata } = fileMetadata || {};

    const extractCsvTypeHeaders = (optionsArray) =>
      optionsArray.map(({ label, value }) => {
        const fieldLabel = customMetadata?.[value] || label || value;
        return { label: fieldLabel, field: value, align: "inherit" };
      });

    const officeCsvFieldOptionsMap = {
      "purchased-water-csv": waterCsvFieldOptions,
      "steam-csv": steamCsvFieldOptions,
      "chill-water-csv": chillWaterCsvFieldOptions,
      "refrigerants-csv": refrigerantsCsvFieldOptions
    };

    if (officeCsvFieldOptionsMap[type]) {
      return extractCsvTypeHeaders(officeCsvFieldOptionsMap[type]);
    }

    // Ticket #391: a general Smart Upload batch's row docs don't carry
    // taskDataColumnNames (that field is only ever written by the
    // unrelated events-attendees-CSV family) and the batch's task doc has
    // no single file to resolve fileMetadata from (it's a GCS folder
    // prefix, not a file path) -- so without this branch, ahead of the
    // fileMetadata guard below, the error table always fell through to []
    // and showed only a Status/Error column with nothing identifying
    // which file failed.
    if (isGeneralSmartUpload) {
      return [{ label: "File Name", field: "fileName", align: "inherit" }];
    }

    if (!fileMetadata) {
      return [];
    }

    if (type === "transactions") {
      return extractCsvTypeHeaders(transactionsFieldOptions);
    }

    if (type === "shipping-transactions") {
      return extractCsvTypeHeaders(shippingFieldOptions);
    }

    // Ticket #372 (Import-path consolidation): these three CSV handlers now
    // write type: "smart-upload" instead of their old, type-keyed values
    // above -- without this branch they'd silently collapse onto the
    // generic taskDataColumnNames fallback below, losing their friendly
    // per-family column labels the moment their type value changed. Reuses
    // tasks.js's own SMART_UPLOAD_FIELD_OPTIONS_BY_FAMILY (Phase 3 PR review
    // finding) instead of keeping a second, separately-maintained copy.
    if (
      type === "smart-upload" &&
      SMART_UPLOAD_FIELD_OPTIONS_BY_FAMILY.has(documentFamily)
    ) {
      return extractCsvTypeHeaders(
        SMART_UPLOAD_FIELD_OPTIONS_BY_FAMILY.get(documentFamily)
      );
    }

    // For dynamic-header task types (e.g. event-attendees-csv), the CSV was
    // parsed with its raw column headers as keys (csvtojson), and
    // taskDataColumnNames maps each canonical field to that raw header --
    // so the row is actually keyed by the mapped header (displayName),
    // not by the canonical field name. storageKey must point at the real
    // key, distinct from field (used only for the type lookup below).
    return Object.entries(taskDataColumnNames || {}).map(
      ([field, displayName]) => ({
        label: displayName,
        field: field,
        storageKey: displayName,
        align: "inherit"
      })
    );
  };

  const taskDataColumnHeaderObjs = buildTasksDataColumnHeaderObjs();

  const actionsHeaderObj = isEditing
    ? [{ label: "", field: "actions", align: "right" }]
    : [];

  const tableHeaders = [
    ...statusHeaderObj,
    ...taskDataColumnHeaderObjs,
    { label: "Error", field: "errorMessage", align: "inherit" },
    ...actionsHeaderObj
  ];

  const generateDefaultDisplayedRows = useCallback(
    () =>
      erroredTaskDataRows
        .filter(({ ignoreInReprocess }) => !ignoreInReprocess)
        .map((row, idx) => ({
          ...row,
          displayedRowIndex: idx,
          isEdited: false,
          ignoreInReprocess: false,
          taskType: type
        })),
    [erroredTaskDataRows, type]
  );

  useEffect(() => {
    if (erroredTaskDataRows) {
      setDisplayedRows(generateDefaultDisplayedRows());
    }
  }, [erroredTaskDataRows, generateDefaultDisplayedRows]);

  useEffect(() => {
    if (filePath && !fileMetadata) {
      getFileMetadata(filePath, fileBucket).then(setFileMetadata);
    }
  }, [filePath, fileMetadata, fileBucket]);

  const handleArchiveTask = () => {
    setIsArchiving(true);

    return fetchOurApi({
      path: "/transactions/archive-task-and-task-transactions",
      method: "POST",
      data: {
        companyId,
        taskDocId
      },
      callback: () => {
        activateSnackbar({
          message:
            "The process and it's associated transactions have been deleted.",
          alert: "success"
        });

        return onClose();
      },
      setError: () => {
        activateSnackbar({
          message: "Failed to archive process and transactions.",
          alert: "error"
        });

        return setIsArchiving(false);
      }
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));

    return setPage(0);
  };

  const handleCellEdit = (editRowDataFunc, columnName, value) => {
    editRowDataFunc(columnName, value);

    return editRowDataFunc("isEdited", true);
  };

  const handleIgnoreRow = (editRowDataFunc, currentIgnoreInReprocess) =>
    editRowDataFunc("ignoreInReprocess", !currentIgnoreInReprocess);

  const handleResetChanges = () =>
    setDisplayedRows(generateDefaultDisplayedRows());

  const reprocessReviewDrivenCsv = async () => {
    const response = await retryErroredCsvRows({
      companyId,
      taskDocId,
      edits: buildCsvRetryEdits({
        displayedRows,
        originalRows: erroredTaskDataRows
      })
    });
    setIsSaving(false);

    if (!response || response.error) {
      return activateSnackbar({
        message:
          response?.message || "Failed to save changes. Please try again.",
        alert: "error"
      });
    }

    activateSnackbar({
      message: "The task is being reprocessed with your modifications.",
      alert: "success"
    });
    return onClose();
  };

  const handleReprocessErrorRows = async () => {
    setIsSaving(true);

    if (isReviewDrivenCsv) {
      return reprocessReviewDrivenCsv();
    }

    try {
      const modifiedRows = displayedRows.filter(
        (row) => row.isEdited || row.ignoreInReprocess
      );

      await Promise.all(
        modifiedRows.map(
          ({ id, isEdited, ignoreInReprocess, ...otherRowData }) =>
            updateCollectionDoc(`tasks/${taskDocId}/taskData`, id, {
              ...otherRowData,
              isEdited,
              ignoreInReprocess
            })
        )
      );

      await updateCollectionDoc("tasks", taskDocId, {
        isReprocessing: true,
        currentChunk: 0,
        status: "running"
      });

      activateSnackbar({
        message: "The task is being reprocessed with your modifications.",
        alert: "success"
      });

      onClose();
    } catch (error) {
      console.error("Error saving changes:", error);

      activateSnackbar({
        message: "Failed to save changes. Please try again.",
        alert: "error"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const paginatedRows = displayedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleDownloadErrorRowsCsv = () => {
    try {
      const downloadErrorRows = erroredTaskDataRows.filter(
        ({ ignoreInReprocess }) => !ignoreInReprocess
      );
      const csv = json2csv(downloadErrorRows);

      const csvBlob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(csvBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `error-rows-${fileName}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      activateSnackbar({
        message: "Failed to download error rows CSV.",
        alert: "error"
      });
    }
  };

  return (
    <SlideLayout
      isSlideOpen={!!task}
      setIsSlideOpen={onClose}
      header={
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Grid container justifyContent="center" alignItems="center">
              <Avatar style={{ backgroundColor: color }}>{Icon}</Avatar>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container direction="column">
              <Grid item>
                <Typography variant="body1">{`${date} - ${fileName}`}</Typography>
              </Grid>
              <Grid item>
                <Typography variant="body2">{summary}</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      }
      content={
        <MuiCard style={{ backgroundColor: palette.backgroundGray.main }}>
          {isErroredTaskDataLoading ? (
            <CardContent>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={2}
              >
                <Typography variant="body1">Loading task data...</Typography>
              </Box>
            </CardContent>
          ) : hasErrors ? (
            <>
              <CardHeader
                title="Emissions To Fix"
                action={
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      <Tooltip title="Download Error Rows CSV">
                        <IconButton
                          onClick={handleDownloadErrorRowsCsv}
                          size="large"
                        >
                          <FileDownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                    {!isGeneralSmartUpload && (
                      <Grid item>
                        <Tooltip title="Reset Changes">
                          <IconButton onClick={handleResetChanges} size="large">
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    )}
                    {!isGeneralSmartUpload && (
                      <Grid item>
                        <Tooltip
                          title={isEditing ? "Save Changes" : "Edit Rows"}
                        >
                          <IconButton
                            onClick={() => setIsEditing(!isEditing)}
                            size="large"
                          >
                            {isEditing ? <SaveIcon /> : <EditIcon />}
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    )}
                  </Grid>
                }
              />
              <CardContent>
                <TableContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        {tableHeaders.map(({ label, align }, idx) => (
                          <TableCell
                            key={`task-data-header-${idx}`}
                            align={align}
                          >
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedRows.map((row, rowIndex) => (
                        <ErroredTaskDataRow
                          key={`task-data-row-${rowIndex}`}
                          row={row}
                          tableHeaders={tableHeaders}
                          isEditing={isEditing}
                          handleCellEdit={handleCellEdit}
                          handleIgnoreRow={handleIgnoreRow}
                          setDisplayedRows={setDisplayedRows}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box display="flex" justifyContent="flex-end">
                  <TablePagination
                    component="div"
                    count={displayedRows.length}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                </Box>
              </CardContent>
              {hasModifiedRows && !isEditing && !isGeneralSmartUpload && (
                <CardActions style={{ justifyContent: "center" }}>
                  <LoadingButton
                    isLoading={isSaving}
                    label="Reprocess Error Rows"
                    color="primary"
                    onClick={handleReprocessErrorRows}
                  />
                </CardActions>
              )}
            </>
          ) : (
            <>
              <CardHeader title="Upload Summary" />
              <CardContent>
                <Grid container spacing={2} direction="column">
                  <Grid item>
                    <Typography variant="body2" color="textSecondary">
                      Start Time
                    </Typography>
                    <Typography variant="body1">
                      {formatTimestamp(startTime)}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" color="textSecondary">
                      End Time
                    </Typography>
                    <Typography variant="body1">
                      {endTime ? formatTimestamp(endTime) : "—"}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" color="textSecondary">
                      Rows Processed
                    </Typography>
                    <Typography variant="body1">
                      {totalTasksProcessed ?? 0} / {totalTasks ?? 0}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </>
          )}
        </MuiCard>
      }
      footer={
        <Grid container direction="column" spacing={2}>
          {showDeleteWarning && (
            <Grid item>
              <WarningPopup
                warningTitle="Are you sure you would like to delete this process and all transactions associated with it? This action cannot be undone."
                warningInput={
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <LoadingButton
                      isLoading={isArchiving}
                      label="Yes, I would like to delete"
                      onClick={handleArchiveTask}
                    />
                  </Box>
                }
                backgroundColor={palette.error.main}
              />
            </Grid>
          )}
          <Grid
            item
            container
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item>
              <Tooltip title="Delete this process and all transactions associated with it">
                <span>
                  <IconButton
                    size="large"
                    onClick={() => setShowDeleteWarning((prev) => !prev)}
                    disabled={isArchiving}
                  >
                    <DeleteForeverIcon color="error" />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
            {filePath && fileBucket && (
              <Grid item>
                <LoadingButton
                  isLoading={false}
                  label="Download Full CSV"
                  color="primary"
                  disabled={false}
                  onClick={() => handleDownload(task)}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      }
    />
  );
};

const ProcessLogTable = ({ taskDocs, error, onReviewTask }) => {
  const [selectedTask, setSelectedTask] = useState(null);

  const filteredAndSortedTaskDocs = taskDocs
    .filter(({ archived }) => !archived)
    .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

  const processTableColumns = [
    makeColumnObj("STATUS", "status", true),
    makeColumnObj("START TIME", "startTime", true),
    makeColumnObj("TYPE", "type", true),
    makeColumnObj("FILE NAME", "fileName", true),
    makeColumnObj("TASK ID", "id", true),
    makeColumnObj("ACTION", "action", false)
  ];

  return error ? (
    <Box display="flex" justifyContent="center" alignItems="center" p={2}>
      <Typography color="error">Failed to load process logs.</Typography>
    </Box>
  ) : filteredAndSortedTaskDocs.length === 0 ? (
    <Box display="flex" justifyContent="center" alignItems="center" p={2}>
      <Typography variant="body1">No process logs found.</Typography>
    </Box>
  ) : (
    <>
      <SortableTable
        rows={filteredAndSortedTaskDocs}
        rowComponent={(taskDoc) => (
          <TaskDocRow
            key={`task-doc-${taskDoc.id}`}
            taskDoc={taskDoc}
            onViewSummary={() => setSelectedTask(taskDoc)}
            onReview={() => onReviewTask(taskDoc.id)}
          />
        )}
        columns={processTableColumns}
      />
      {selectedTask && (
        <TaskDetailSlide
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
        />
      )}
    </>
  );
};

const AccountActivity = () => {
  const [auditLogs, isAuditLogsLoading, error] = useCompanyAuditLogs();
  const [taskDocs, taskDocsLoading] = useCollectionDataListener("tasks");
  const { activateSnackbar } = usePlatformLayoutContext();

  const selectedView = getUrlSearchParams("view");

  const [selectedTab, setSelectedTab] = useState(
    selectedView === "audit-logs" ? "audit-logs" : "process-logs"
  );
  // #375's "ready to review" email links here with ?smartUploadTaskId= and
  // ?companyId=. The companyId is a sanity check: if it doesn't match the
  // account the recipient is actually logged into, the task doc wouldn't
  // belong to this account's "tasks" subcollection anyway, so warn instead
  // of silently handing SmartUploadReviewDialog a taskDocId it can't find.
  const reviewLinkCompanyId = getUrlSearchParams("companyId");
  const isReviewLinkForDifferentAccount =
    !!reviewLinkCompanyId && reviewLinkCompanyId !== getAccountId();
  const [reviewTaskId, setReviewTaskId] = useState(
    isReviewLinkForDifferentAccount
      ? null
      : getUrlSearchParams("smartUploadTaskId")
  );

  useEffect(() => {
    if (!isReviewLinkForDifferentAccount) {
      return;
    }

    activateSnackbar({
      message:
        "That upload review link is for a different Aclymate account. Please log in with the account that received the email.",
      alert: "error"
    });
  }, [isReviewLinkForDifferentAccount, activateSnackbar]);

  return (
    <FeatureErrorBoundary featureName="Account Activity">
      <Card
        cardType="layout"
        gridProps={{ sm: 12 }}
        title={selectedTab === "audit-logs" ? "Audit Logs" : "Process Logs"}
        subtitle="Track all changes and processing tasks performed in your account"
        icon={<HistoryIcon />}
        isLoading={
          selectedTab === "audit-logs" ? isAuditLogsLoading : taskDocsLoading
        }
        action={
          <ToggleButtons
            value={selectedTab}
            onChange={setSelectedTab}
            buttons={[
              {
                value: "process-logs",
                name: "Process Logs",
                id: "process-logs-toggle"
              },
              {
                value: "audit-logs",
                name: "Audit Logs",
                id: "audit-logs-toggle"
              }
            ]}
          />
        }
      >
        {selectedTab === "audit-logs" ? (
          <AuditLogTable auditLogs={auditLogs} error={error} />
        ) : (
          <ProcessLogTable
            taskDocs={taskDocs}
            error={error}
            onReviewTask={setReviewTaskId}
          />
        )}
      </Card>
      {reviewTaskId && (
        <SmartUploadReviewDialog
          companyId={getAccountId()}
          taskDocId={reviewTaskId}
          onClose={() => setReviewTaskId(null)}
        />
      )}
    </FeatureErrorBoundary>
  );
};

export default AccountActivity;
